/*
  Bundle draft-order checkout for PureAmor.

  When the cart contains lines tagged with the hidden `_pa_bundle_key`
  property (added by sections/pureamor-build-your-own-bundle.liquid), the
  checkout click is intercepted and the whole cart is sent to the Supabase
  `bundle-draft-checkout` edge function, which validates the bundle, creates
  a Shopify Draft Order with line-level percentage discounts and returns the
  draft order invoice URL. The customer pays on Shopify's standard checkout
  page (Apple Pay / wallets included), so no Shopify Plus is required.

  Checkout clicks are always intercepted and routed from the live cart
  state: carts without bundle lines continue straight to the native Shopify
  checkout (the original cart form is re-submitted, keeping notes and
  attributes), so there is no activation race. If the backend cannot be
  reached while a bundle IS in the cart, checkout shows a retryable error
  instead of silently falling back to the full-price native checkout.

  Also responsible for:
  - Grouping bundle component lines into one visual summary row in the cart
    drawer (quick-cart) and the cart page, with estimated savings.
  - Replacing the visible subtotal with the bundle-adjusted estimate.
  - Hiding accelerated checkout buttons (Apple Pay / wallet buttons rendered
    by content_for_additional_checkout_buttons) while a bundle is in the
    cart — those buttons bypass the draft-order flow and would charge full
    price. They come back as soon as the bundle is removed.
  - Keeping the free-gift line (hidden `_free_gift` property, normally made
    free by the Shopify Function discount) free inside the draft order by
    tagging it as a 100%-discounted bundle gift in the checkout payload.

  Backend rules live in supabase/functions/bundle-draft-checkout/index.ts
  (project: pureamor-bundle-checkout). Theme tiers and backend tiers must be
  kept in sync — the backend is the source of truth for the final discount.
*/

(function () {
  var ENDPOINT = window.PUREAMOR_BUNDLE_DRAFT_CHECKOUT_URL || 'https://cvgujbudnaovsebfirgw.supabase.co/functions/v1/bundle-draft-checkout';
  var submitting = false;
  var suppressCartObserver = false;
  var refreshTimer = null;

  var BUNDLE_RULES = {
    'build-your-own-bundle': { title: 'Build Your Own Bundle', percent: 42 }
  };

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD'
      }).format((Number(cents) || 0) / 100);
    } catch (_error) {
      return '$' + (((Number(cents) || 0) / 100).toFixed(2));
    }
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, function (character) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      }[character];
    });
  }

  function normalizeProperties(properties) {
    if (!properties || typeof properties !== 'object') return {};
    return Object.keys(properties).reduce(function (result, key) {
      var value = properties[key];
      if (value !== null && typeof value !== 'undefined' && String(value).trim()) {
        result[key] = String(value);
      }
      return result;
    }, {});
  }

  function hasBundleLines(cart) {
    return Boolean(cart && Array.isArray(cart.items) && cart.items.some(function (item) {
      return item.properties && item.properties._pa_bundle_key;
    }));
  }

  function bundleGroupId(item) {
    var properties = normalizeProperties(item.properties);
    var bundleKey = properties._pa_bundle_key;
    if (!bundleKey) return '';
    return bundleKey + ':' + (properties._pa_bundle_instance || 'default');
  }

  function bundleLineDiscount(item) {
    var properties = normalizeProperties(item.properties);
    var rule = BUNDLE_RULES[properties._pa_bundle_key];
    var linePrice = Number(item.final_line_price || item.line_price || 0);
    var percent = properties._pa_bundle_role === 'free-gift'
      ? 100
      : Number(properties._pa_bundle_discount_percent || (rule && rule.percent) || 0);
    return Math.round(linePrice * percent / 100);
  }

  function bundleGroups(cart) {
    var groups = {};
    (cart.items || []).forEach(function (item) {
      var groupId = bundleGroupId(item);
      if (!groupId) return;
      var properties = normalizeProperties(item.properties);
      var rule = BUNDLE_RULES[properties._pa_bundle_key] || {};
      if (!groups[groupId]) {
        groups[groupId] = {
          id: groupId,
          key: properties._pa_bundle_key,
          title: properties._pa_bundle_title || rule.title || 'PureAmor bundle',
          percent: Number(properties._pa_bundle_discount_percent || rule.percent || 0),
          instance: properties._pa_bundle_instance || 'default',
          image: '',
          items: [],
          quantity: 0,
          subtotal: 0,
          discount: 0
        };
      }
      if (!groups[groupId].image && item.image) groups[groupId].image = item.image;
      groups[groupId].items.push(item);
      groups[groupId].quantity += Number(item.quantity || 0);
      groups[groupId].subtotal += Number(item.final_line_price || item.line_price || 0);
      groups[groupId].discount += bundleLineDiscount(item);
    });
    return Object.keys(groups).map(function (key) { return groups[key]; });
  }

  function safeSelector(value) {
    if (window.CSS && CSS.escape) return CSS.escape(value);
    return String(value).replace(/"/g, '\\"');
  }

  function ensureBundleCartStyles() {
    if (document.querySelector('[data-pa-bundle-cart-style]')) return;
    var style = document.createElement('style');
    style.setAttribute('data-pa-bundle-cart-style', '');
    style.textContent = [
      '.pa-bundle-cart-summary{display:grid;grid-template-columns:72px minmax(0,1fr);gap:12px;padding:14px 0;border-bottom:1px solid var(--color-border,rgba(0,0,0,.1));}',
      '.quick-cart__items .pa-bundle-cart-summary{grid-template-columns:64px minmax(0,1fr);}',
      '.pa-bundle-cart-summary__media{border-radius:8px;background:var(--color-background-footer,#ebf0f2);min-height:72px;display:grid;place-items:center;color:var(--color-text,#102b26);font-size:11px;letter-spacing:.08em;text-transform:uppercase;overflow:hidden;}',
      '.pa-bundle-cart-summary__media img{width:100%;height:100%;object-fit:cover;display:block;}',
      '.pa-bundle-cart-summary__body{display:grid;gap:6px;align-content:start;}',
      '.pa-bundle-cart-summary__title{margin:0;font-weight:700;line-height:1.25;}',
      '.pa-bundle-cart-summary__meta,.pa-bundle-cart-summary__note{margin:0;opacity:.65;font-size:12px;line-height:1.35;}',
      '.pa-bundle-cart-summary__prices{display:flex;gap:8px;align-items:baseline;flex-wrap:wrap;font-size:13px;}',
      '.pa-bundle-cart-summary__compare{text-decoration:line-through;opacity:.55;}',
      '.pa-bundle-cart-summary__price{font-weight:700;}',
      '.pa-bundle-cart-summary__save{color:var(--color-products-sale-price,#d21404);font-size:12px;}',
      '.pa-bundle-cart-summary__remove{justify-self:start;border:0;background:transparent;padding:0;color:inherit;opacity:.7;text-decoration:underline;cursor:pointer;font:inherit;font-size:12px;}',
      '.pa-bundle-cart-summary__remove:hover{opacity:1;}',
      '[data-pa-hidden-bundle-line="true"]{display:none!important;}',
      /* Wallet / accelerated checkout buttons skip the draft-order flow, so
         hide them while a bundle is in the cart. */
      'body[data-pa-bundle-checkout="draft"] .additional-checkout-buttons{display:none!important;}'
    ].join('\n');
    document.head.appendChild(style);
  }

  function summaryHtml(group, currency) {
    var total = Math.max(0, group.subtotal - group.discount);
    return '' +
      '<div class="pa-bundle-cart-summary" data-pa-bundle-cart-summary="' + escapeHtml(group.id) + '">' +
        '<div class="pa-bundle-cart-summary__media" aria-hidden="true">' +
          (group.image ? '<img src="' + escapeHtml(group.image) + '" alt="">' : 'Bundle') +
        '</div>' +
        '<div class="pa-bundle-cart-summary__body">' +
          '<p class="pa-bundle-cart-summary__title fs-product-card-title">' + escapeHtml(group.title) + '</p>' +
          '<p class="pa-bundle-cart-summary__meta">' + group.quantity + ' bundle item' + (group.quantity === 1 ? '' : 's') + (group.percent ? ' · ' + group.percent + '% off' : '') + '</p>' +
          '<div class="pa-bundle-cart-summary__prices">' +
            '<span class="pa-bundle-cart-summary__compare">' + money(group.subtotal, currency) + '</span>' +
            '<span class="pa-bundle-cart-summary__price">' + money(total, currency) + '</span>' +
            '<span class="pa-bundle-cart-summary__save">Save ' + money(group.discount, currency) + '</span>' +
          '</div>' +
          '<p class="pa-bundle-cart-summary__note">Bundle savings are applied on the secure checkout page.</p>' +
          '<button type="button" class="pa-bundle-cart-summary__remove" data-pa-remove-bundle="' + escapeHtml(group.id) + '">Remove bundle</button>' +
        '</div>' +
      '</div>';
  }

  function renderBundleCartSummaries(cart) {
    ensureBundleCartStyles();
    suppressCartObserver = true;
    document.querySelectorAll('[data-pa-bundle-cart-summary]').forEach(function (summary) { summary.remove(); });
    document.querySelectorAll('[data-pa-bundle-cart-total-preview]').forEach(function (summary) { summary.remove(); });
    document.querySelectorAll('[data-pa-hidden-bundle-line="true"]').forEach(function (line) {
      line.removeAttribute('data-pa-hidden-bundle-line');
    });

    var groups = bundleGroups(cart);
    groups.forEach(function (group) {
      var firstLinesByParent = [];
      group.items.forEach(function (item) {
        var lines = document.querySelectorAll('[data-input-item][data-key="' + safeSelector(item.key) + '"]');
        lines.forEach(function (line) {
          if (!line) return;
          var parent = line.parentNode;
          if (parent && !firstLinesByParent.some(function (entry) { return entry.parent === parent; })) {
            firstLinesByParent.push({ parent: parent, line: line });
          }
          line.setAttribute('data-pa-hidden-bundle-line', 'true');
        });
      });
      firstLinesByParent.forEach(function (entry) {
        entry.line.insertAdjacentHTML('beforebegin', summaryHtml(group, cart.currency));
      });
    });

    renderBundleTotalPreview(cart, groups);
    window.setTimeout(function () { suppressCartObserver = false; }, 120);
  }

  function subtotalTargets() {
    return Array.from(document.querySelectorAll('.quick-cart__footer-subtotal > span, .cart__footer-subtotal > span'));
  }

  function restoreSubtotalDisplays() {
    subtotalTargets().forEach(function (target) {
      var original = target.getAttribute('data-pa-original-subtotal-text');
      if (original) target.textContent = original;
      target.removeAttribute('data-pa-original-subtotal-text');
      target.removeAttribute('data-pa-bundle-adjusted-subtotal');
    });
  }

  function updateSubtotalDisplays(cart, discount) {
    if (!discount) {
      restoreSubtotalDisplays();
      return;
    }
    var estimatedTotal = Math.max(0, Number(cart.total_price || 0) - discount);
    subtotalTargets().forEach(function (target) {
      if (!target.getAttribute('data-pa-original-subtotal-text')) {
        target.setAttribute('data-pa-original-subtotal-text', target.textContent.trim());
      }
      target.setAttribute('data-pa-bundle-adjusted-subtotal', 'true');
      target.textContent = money(estimatedTotal, cart.currency);
    });
  }

  function renderBundleTotalPreview(cart, groups) {
    var discount = groups.reduce(function (total, group) { return total + group.discount; }, 0);
    updateSubtotalDisplays(cart, discount);
    if (!discount) return;
    var html = '' +
      '<div data-pa-bundle-cart-total-preview style="display:grid;gap:5px;margin:10px 0 4px;padding:10px 0;border-top:1px solid var(--color-border,rgba(0,0,0,.1));border-bottom:1px solid var(--color-border,rgba(0,0,0,.1));">' +
        '<div style="display:flex;justify-content:space-between;gap:12px;font-size:13px;line-height:1.35;">' +
          '<span>Bundle savings at checkout</span>' +
          '<strong style="color:var(--color-products-sale-price,#d21404);">-' + money(discount, cart.currency) + '</strong>' +
        '</div>' +
      '</div>';
    var targets = document.querySelectorAll('.quick-cart__footer-subtotal, .cart__footer-subtotal');
    targets.forEach(function (target) {
      target.insertAdjacentHTML('beforebegin', html);
    });
  }

  async function removeBundle(groupId, button) {
    if (button) {
      button.disabled = true;
      button.textContent = 'Removing...';
    }
    var cart = await fetchCart();
    var group = bundleGroups(cart).filter(function (item) { return item.id === groupId; })[0];
    if (!group) return;
    var updates = {};
    group.items.forEach(function (item) {
      updates[item.key] = 0;
    });
    await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    });
    window.location.reload();
  }

  function checkoutButtons() {
    return Array.from(document.querySelectorAll('button[name="checkout"], input[name="checkout"], a[href="/checkout"], a[href$="/checkout"]'));
  }

  function setButtonsBusy(isBusy) {
    checkoutButtons().forEach(function (button) {
      if (isBusy) {
        button.setAttribute('aria-busy', 'true');
        if (button.tagName === 'BUTTON' || button.tagName === 'INPUT') {
          button.setAttribute('data-pa-original-disabled', button.disabled ? 'true' : 'false');
          button.disabled = true;
        }
      } else {
        button.removeAttribute('aria-busy');
        if ((button.tagName === 'BUTTON' || button.tagName === 'INPUT') && button.getAttribute('data-pa-original-disabled') !== 'true') {
          button.disabled = false;
        }
        button.removeAttribute('data-pa-original-disabled');
      }
    });
  }

  function showError(message) {
    var note = document.querySelector('[data-pa-bundle-checkout-error]');
    if (!note) {
      note = document.createElement('div');
      note.setAttribute('data-pa-bundle-checkout-error', '');
      note.style.marginTop = '12px';
      note.style.color = '#a32323';
      note.style.fontSize = '13px';
      note.style.lineHeight = '1.35';
      var target = document.querySelector('.cart__input-buttons, .quick-cart__submit') || document.querySelector('form[action="/cart"]');
      if (target && target.parentNode) target.parentNode.insertBefore(note, target.nextSibling);
    }
    note.textContent = message || 'Unable to prepare bundle checkout. Please try again.';
  }

  function clearError() {
    document.querySelectorAll('[data-pa-bundle-checkout-error]').forEach(function (note) {
      note.textContent = '';
    });
  }

  function buildPayload(cart) {
    // The theme free gift (hidden `_free_gift` property) is normally made
    // free by the Shopify Function discount, but automatic discounts do not
    // apply to draft orders. Tag it as a bundle gift so the backend gives it
    // a 100% line discount instead. Gift-role lines are excluded from bundle
    // size validation on the backend.
    var firstGroup = bundleGroups(cart)[0] || null;

    return {
      cart: {
        token: cart.token || '',
        currency: cart.currency || '',
        items: (cart.items || []).map(function (item) {
          var properties = normalizeProperties(item.properties);
          if (firstGroup && properties._free_gift && !properties._pa_bundle_key) {
            properties._pa_bundle_key = firstGroup.key;
            properties._pa_bundle_instance = firstGroup.instance;
            properties._pa_bundle_role = 'free-gift';
          }
          return {
            id: item.id,
            variant_id: item.variant_id || item.id,
            quantity: item.quantity,
            title: item.title,
            properties: properties
          };
        })
      }
    };
  }

  async function fetchCart() {
    var response = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Unable to read cart.');
    return response.json();
  }

  async function createDraftCheckout(cart) {
    var response = await fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(buildPayload(cart))
    });
    var payload = await response.json().catch(function () { return {}; });
    if (!response.ok || payload.ok === false) {
      throw new Error(payload.error || 'Unable to prepare bundle checkout.');
    }
    return payload;
  }

  function failCheckout(message) {
    showError(message);
    submitting = false;
    setButtonsBusy(false);
  }

  function nativeCheckout(trigger) {
    var form = trigger && (trigger.form || (trigger.closest ? trigger.closest('form') : null));
    if (form && /\/cart\/?$/.test(form.getAttribute('action') || '')) {
      // Preserve the native flow (cart note / attributes post together with
      // the form). form.submit() skips submit listeners, so this cannot
      // re-enter the interceptor; the hidden field stands in for the
      // submitter's name="checkout".
      if (!form.querySelector('input[type="hidden"][name="checkout"]')) {
        var checkoutField = document.createElement('input');
        checkoutField.type = 'hidden';
        checkoutField.name = 'checkout';
        checkoutField.value = '';
        form.appendChild(checkoutField);
      }
      form.submit();
      return;
    }
    window.location.href = '/checkout';
  }

  async function prepareCheckout(event, trigger) {
    if (submitting) return;
    if (!trigger) return;
    submitting = true;

    var cart;
    try {
      cart = await fetchCart();
    } catch (_error) {
      // Cart unreadable. If the last known state had a bundle, refuse to
      // proceed (native checkout would silently charge the undiscounted
      // price); otherwise continue the native flow.
      if (lastCartHadBundle) {
        failCheckout('Unable to prepare bundle checkout. Please try again.');
      } else {
        nativeCheckout(trigger);
      }
      return;
    }

    syncBundleState(cart);

    // No bundle in the cart — continue the native Shopify checkout.
    if (!hasBundleLines(cart)) {
      nativeCheckout(trigger);
      return;
    }

    clearError();
    setButtonsBusy(true);

    // The backend rejects carts over its hard limits (50 units per line,
    // 200 lines) with a generic error — pre-check so the customer gets an
    // actionable message instead.
    var oversized = (cart.items || []).some(function (item) { return Number(item.quantity) > 50; });
    if (oversized || (cart.items || []).length > 200) {
      failCheckout('Bundle checkout supports up to 50 units per item. Please lower the quantity, or order the bundle and the high-quantity items separately.');
      return;
    }

    try {
      var payload = await createDraftCheckout(cart);
      if (payload.useNativeCheckout) {
        window.location.href = '/checkout';
        return;
      }
      if (!payload.checkoutUrl) {
        throw new Error('Shopify did not return a checkout link.');
      }
      window.location.href = payload.checkoutUrl;
    } catch (error) {
      // Network / CORS failure (Supabase down, origin not in the backend
      // allowlist): show a retryable error. Never silently redirect to the
      // native checkout — it would charge full price for a visibly
      // discounted cart.
      failCheckout(error instanceof TypeError
        ? 'We could not apply your bundle discount. Please try again in a moment.'
        : (error && error.message ? error.message : 'Unable to prepare bundle checkout. Please try again.'));
    }
  }

  // ── Checkout routing ──────────────────────────────────────────────────────
  // The capture-phase listeners are ALWAYS registered; the draft-vs-native
  // decision is made per click from the live cart state. This closes the
  // race where a checkout click could land before the first async /cart.js
  // read had activated a conditional interceptor. Carts without bundle lines
  // continue to the native checkout via nativeCheckout(), which re-submits
  // the original cart form so notes/attributes are preserved.

  var lastCartHadBundle = false;

  function onCheckoutClick(event) {
    var trigger = event.target && event.target.closest
      ? event.target.closest('button[name="checkout"], input[name="checkout"], a[href="/checkout"], a[href$="/checkout"]') : null;
    if (!trigger) return;
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    prepareCheckout(event, trigger);
  }

  function onCheckoutSubmit(event) {
    var submitter = event.submitter;
    if (submitter && submitter.name === 'checkout') {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      prepareCheckout(event, submitter);
    }
  }

  // Always-on: "Remove bundle" button inside the cart summary rows.
  document.addEventListener('click', function (event) {
    var removeButton = event.target && event.target.closest
      ? event.target.closest('[data-pa-remove-bundle]') : null;
    if (!removeButton) return;
    event.preventDefault();
    removeBundle(removeButton.getAttribute('data-pa-remove-bundle'), removeButton);
  }, true);

  // Checkout listeners are registered unconditionally (capture phase) so no
  // click can slip through before cart state is known.
  document.addEventListener('click', onCheckoutClick, true);
  document.addEventListener('submit', onCheckoutSubmit, true);

  // Tracks the last cart state we saw: drives the wallet-button hiding CSS
  // hook and the fallback decision when /cart.js is unreadable at click time.
  function syncBundleState(cart) {
    lastCartHadBundle = hasBundleLines(cart);
    if (lastCartHadBundle) {
      document.body.setAttribute('data-pa-bundle-checkout', 'draft');
    } else {
      document.body.removeAttribute('data-pa-bundle-checkout');
    }
  }

  // ── Cart refresh (summaries + bundle state) ───────────────────────────────

  async function refreshBundleCartSummaries() {
    try {
      var cart = await fetchCart();
      renderBundleCartSummaries(cart);
      syncBundleState(cart);
    } catch (_error) {}
  }

  function scheduleBundleCartRefresh(delay) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refreshBundleCartSummaries, delay || 250);
  }

  document.addEventListener('cart:updated', function (event) {
    if (event.detail && event.detail.cart) {
      renderBundleCartSummaries(event.detail.cart);
      syncBundleState(event.detail.cart);
    } else {
      refreshBundleCartSummaries();
    }
    scheduleBundleCartRefresh(350);
    scheduleBundleCartRefresh(900);
  });

  document.addEventListener('quick-cart:updated', function () {
    scheduleBundleCartRefresh(250);
    scheduleBundleCartRefresh(700);
  });

  var observer = new MutationObserver(function (mutations) {
    if (suppressCartObserver) return;
    var shouldRefresh = mutations.some(function (mutation) {
      var target = mutation.target;
      return target && target.closest && (
        target.closest('.quick-cart') ||
        target.closest('.cart') ||
        (target.matches && target.matches('.quick-cart, .cart'))
      );
    });
    if (shouldRefresh) scheduleBundleCartRefresh(180);
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshBundleCartSummaries);
  } else {
    refreshBundleCartSummaries();
  }
})();
